import get from 'lodash/get';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import range from 'lodash/range';

import { DialogTitleWithClose, RangeSlider, GroupedSelect } from 'ui';
import { useGroupedAutocompleteStyles } from 'shared/styles/groupedAutocompleteStyles/groupedAutocompleteStyles';
import { getSortedFlattenedIndustries } from 'selectors/common';
import { useRegions } from 'shared/hooks';

const ESGEntitiesFilterModal = ({
  open,
  onClose,
  onSubmit,
  searchQuery,
  initialFilter,
  filterableIndustries,
  allowEmptyIndustriesFilter,
}) => {
  const groupedAutocompleteClasses = useGroupedAutocompleteStyles();
  const { t } = useTranslation();
  const industriesData = useSelector(getSortedFlattenedIndustries);
  const regions = useRegions(searchQuery);

  const creditScoreValues = range(11).map((level, index) => ({
    value: index + 1,
    label: level.toString(),
  }));

  const initialScore = () =>
    creditScoreValues.length
      ? [creditScoreValues[0].value, creditScoreValues[creditScoreValues.length - 1].value]
      : [];

  const [values, setValues] = useState({
    countries: [],
    industries: [],
    environment: initialScore(),
    social: initialScore(),
    governance: initialScore(),
    totalScore: initialScore(),
  });

  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    setValues({
      ...values,
      ...initialFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  useEffect(() => {
    setIndustries(filterableIndustries || industriesData);
  }, [industriesData, filterableIndustries]);

  const handleChange = (name) => (value) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleIndustriesChange = (_event, sectors) => {
    let newIndustries = sectors;
    if (!allowEmptyIndustriesFilter && !sectors.length) {
      const initialIndustry = get(initialFilter, 'industries[0]');
      newIndustries = initialIndustry ? [initialIndustry] : [];
    }
    setValues({
      ...values,
      industries: newIndustries,
    });
  };

  const handleSubmit = () => {
    const { environment, social, governance, totalScore } = values;

    const cleanedValues = {
      ...values,
      countries: values.countries.map(({ iso }) => iso),
      sectors: values.industries.map(({ name }) => name),
      environmentFrom: encodeURIComponent(creditScoreValues[environment[0] - 1].label),
      environmentTo: encodeURIComponent(creditScoreValues[environment[1] - 1].label),
      socialFrom: encodeURIComponent(creditScoreValues[social[0] - 1].label),
      socialTo: encodeURIComponent(creditScoreValues[social[1] - 1].label),
      governanceFrom: encodeURIComponent(creditScoreValues[governance[0] - 1].label),
      governanceTo: encodeURIComponent(creditScoreValues[governance[1] - 1].label),
      totalScoreFrom: encodeURIComponent(creditScoreValues[totalScore[0] - 1].label),
      totalScoreTo: encodeURIComponent(creditScoreValues[totalScore[1] - 1].label),
    };

    onSubmit(cleanedValues);
  };

  const handleEnvironmentChange = (_event, newValues) => {
    setValues({
      ...values,
      environment: newValues,
    });
  };

  const handleSocialChange = (_event, newValues) => {
    setValues({
      ...values,
      social: newValues,
    });
  };

  const handleGovernanceChange = (_event, newValues) => {
    setValues({
      ...values,
      governance: newValues,
    });
  };

  const handleTotalScoreChange = (_event, newValues) => {
    setValues({
      ...values,
      totalScore: newValues,
    });
  };

  const isValueRemovable = () => {
    return allowEmptyIndustriesFilter || values.industries.length > 1;
  };

  return (
    <Dialog fullWidth open={open}>
      <DialogTitleWithClose onClose={onClose}>{t('Apply filters')}</DialogTitleWithClose>
      <DialogContent>
        <Box px={2}>
          <GroupedSelect
            multiple
            options={regions.data}
            value={values.countries}
            label={t('Country')}
            onChange={handleChange('countries')}
            getOptionLabel={(option) => option.name}
            groupBy={(option) => option.continent}
          />
          <Autocomplete
            multiple
            options={industries}
            onChange={handleIndustriesChange}
            getOptionLabel={(option) => option.name}
            groupBy={(option) => option.industryName}
            value={values.industries}
            renderInput={(params) => (
              <TextField {...params} label={t('Sectors')} variant="outlined" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  disabled={!isValueRemovable()}
                />
              ))
            }
            classes={groupedAutocompleteClasses}
          />
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Environment')}
              options={creditScoreValues}
              values={values.environment}
              onChange={handleEnvironmentChange}
            />
          </Box>
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Social')}
              options={creditScoreValues}
              values={values.social}
              onChange={handleSocialChange}
            />
          </Box>
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Governance')}
              options={creditScoreValues}
              values={values.governance}
              onChange={handleGovernanceChange}
            />
          </Box>
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Total Score')}
              options={creditScoreValues}
              values={values.totalScore}
              onChange={handleTotalScoreChange}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {t('Apply filters')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ESGEntitiesFilterModal.defaultProps = {
  searchQuery: null,
  initialFilter: {},
  filterableIndustries: null,
  allowEmptyIndustriesFilter: true,
};

ESGEntitiesFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  initialFilter: PropTypes.object,
  filterableIndustries: PropTypes.arrayOf(PropTypes.object),
  allowEmptyIndustriesFilter: PropTypes.bool,
};

export default ESGEntitiesFilterModal;
